import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BlogCommentEntity,
  ServerListResponse,
  ServerSingleResponse,
} from "../../types";
import { AxiosResponse } from "axios";
import { useUserData } from "../useUserData";
import { readerDataStore } from "../../hookstate/readerDataStore";
import { useReleaseData } from "../../screens/reader/hooks/hookstate/useReleaseData";
import { styleUsernamesInComment } from "../../components/comment/utils/styleCommentText";

type CommentPostPromise = Promise<
  AxiosResponse<ServerSingleResponse<BlogCommentEntity>>
>;
type OnSendCommentProps = (data: {
  body: string;
  parentId?: number;
}) => CommentPostPromise;

export function useSendComment(
  queryKey: QueryKey,
  onSendComment: OnSendCommentProps,
) {
  const queryClient = useQueryClient();
  const { userData } = useUserData();
  const { releaseData } = useReleaseData();

  const { mutate } = useMutation({
    mutationFn: ({
      body,
      parentComment: externalParentComment,
    }: {
      body: string;
      parentComment?: BlogCommentEntity;
    }) => {
      // Use external parentComment if provided, otherwise fall back to internal state
      const targetParent = externalParentComment;
      return onSendComment({
        body,
        parentId: targetParent?.id,
      });
    },

    onMutate: ({ body, parentComment: externalParentComment }) => {
      // Use external parentComment if provided, otherwise fall back to internal state
      const targetParent = externalParentComment;
      const prevState = queryClient.getQueryData(queryKey) as AxiosResponse<
        ServerListResponse<BlogCommentEntity>
      >;
      const prevReleaseCommentsData = prevState.data.payload.results;
      const author = userData;

      const commentEntityId = prevReleaseCommentsData[0]?.object_id;
      const commentEntityType = prevReleaseCommentsData[0]?.entity_type;

      if (!author) return;
      const newComment: BlogCommentEntity = {
        author,
        body: body ?? "",
        parsed_body: styleUsernamesInComment(`${body ?? ""}<br />\n`),
        time_ago: "just now",
        post_date: new Date().toISOString().slice(0, 19).replace("T", " "),
        replies: [],

        level: Math.min((targetParent?.level ?? 0) + 1, 2),
        parent_id: targetParent?.id,
        artist_id: 0,
        author_id: 0,
        author_is_context_owner: true,
        can_edit: false,
        can_moderate: false,
        comment_type: "none",
        delete_url: "none",
        edit_url: "none",
        edit_time_ago: null,
        edit_date: null,
        edit_user_id: null,
        entity_type: "none",
        id: Date.now(),
        is_deleted: 0,
        pos: null,
        total_comments: 0,
        page_id: null,
        object_id: 0,
        url: "none",
        total_bad: 0,
        total_good: 0,
        user_vote_reaction_type_id: null,
      };

      const newState = {
        ...prevState,
        data: {
          payload: {
            results: addComment(
              prevReleaseCommentsData,
              newComment,
              targetParent?.id,
            ),
          },
        },
      };

      if (
        releaseData &&
        commentEntityType === "release" &&
        commentEntityId === releaseData?.id
      ) {
        readerDataStore.releaseData.set({
          ...releaseData,
          total_comments: releaseData?.total_comments + 1,
        });
      }

      queryClient.setQueryData(queryKey, newState);

      return newState;
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(queryKey, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return mutate;
}

function addComment(
  comments: BlogCommentEntity[],
  newComment: BlogCommentEntity,
  parentId?: number,
): BlogCommentEntity[] {
  if (parentId === undefined) {
    return [...comments, newComment];
  }

  return comments.map((comment) => {
    if (comment.id === parentId) {
      return {
        ...comment,
        replies: [...comment.replies, newComment],
      };
    }

    if (comment.replies?.length) {
      // Check if any nested comment matches the parentId
      const hasNestedMatch = comment.replies.some(
        (reply) => reply.id === parentId,
      );

      if (hasNestedMatch) {
        if (newComment.level > 1) {
          return {
            ...comment,
            replies: [...comment.replies, newComment],
          };
        }
        return {
          ...comment,
          replies: comment.replies.map((reply) => {
            if (reply.id === parentId) {
              return {
                ...reply,
                replies: [...reply.replies, newComment],
              };
            }
            return reply;
          }),
        };
      }
    }

    return comment;
  });
}
